import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Edit3, Plus } from "lucide-react";
import OrgaAdminGruppeService from "@/lib/orgaAdmin/orgaAdminGruppe.service";
import type { GruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";
import { toast } from "sonner";
import { CreateGroupModal } from "./createGroupModal.component";

type GroupManagementTabProps = {
	organizationId: string;
	groups: GruppeDTO[];
	onReload: () => Promise<void> | void;
};

export function GroupManagementTab({
	organizationId,
	groups,
	onReload,
}: GroupManagementTabProps) {
	const [isCreating, setIsCreating] = useState(false);
	const [editingGroup, setEditingGroup] = useState<GruppeDTO | null>(null);
	const [editTitle, setEditTitle] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	const handleCreate = async (name: string) => {
		const trimmedName = name.trim();
		if (!trimmedName) return;
		setIsCreating(true);
		try {
			await OrgaAdminGruppeService.getInstance().createGruppe(
				organizationId,
				trimmedName,
			);
			setIsCreateOpen(false);
			await onReload();
			toast.success("Gruppe erstellt");
		} catch (error) {
			console.error(error);
			toast.error("Fehler beim Erstellen der Gruppe");
		} finally {
			setIsCreating(false);
		}
	};

	const openEditGroupNameModal = (gruppe: GruppeDTO) => {
		setEditingGroup(gruppe);
		setEditTitle(gruppe.title);
	};

	const handleSaveEdit = async () => {
		if (!editingGroup) return;
		const title = editTitle.trim();
		if (!title) return;
		setIsUpdating(true);
		try {
			await OrgaAdminGruppeService.getInstance().editGruppeTitle(
				organizationId,
				editingGroup.id,
				title,
			);
			setEditingGroup(null);
			await onReload();
			toast.success("Gruppenname aktualisiert");
		} catch (error) {
			console.error(error);
			toast.error("Fehler beim Aktualisieren des Gruppennames");
		} finally {
			setIsUpdating(false);
		}
	};

	return (
		<div className="flex flex-col bg-white rounded-lg p-4 m-4 border flex-1 min-h-0">
			<div className="flex items-center justify-end gap-2 h-12">
				<Button
					onClick={() => setIsCreateOpen(true)}
					disabled={isCreating}
					className="flex items-center gap-2"
				>
					<Plus className="h-4 w-4" />
					Gruppe erstellen
				</Button>
			</div>

			<div className="flex-1 overflow-y-auto mt-4">
				<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg min-h-0">
					<Table>
						<TableHeader className="sticky top-0 bg-white z-10 border-b border-gray-200">
							<TableRow className="bg-gray-50">
								<TableHead>Gruppenname</TableHead>
								<TableHead>Mitglieder</TableHead>
								<TableHead>Klausuren</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{groups.map((g) => (
								<TableRow key={g.id}>
									<TableCell className="font-medium">
										<div className="flex items-center gap-2">
											{g.title}{" "}
											<Edit3
												className="h-4 w-4"
												onClick={() => openEditGroupNameModal(g)}
											/>
										</div>
									</TableCell>
									<TableCell>{g.users?.length ?? 0}</TableCell>
									<TableCell>{g.klausuren?.length ?? 0}</TableCell>
								</TableRow>
							))}
							{groups.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={3}
										className="text-center text-gray-500 py-8"
									>
										Keine Gruppen vorhanden.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			<CreateGroupModal
				open={isCreateOpen}
				onOpenChange={setIsCreateOpen}
				onConfirm={handleCreate}
				isSubmitting={isCreating}
			/>

			<Dialog
				open={!!editingGroup}
				onOpenChange={(open) => !open && setEditingGroup(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Gruppenname bearbeiten</DialogTitle>
						<DialogDescription>
							Passe den Titel der Gruppe an.
						</DialogDescription>
					</DialogHeader>
					<div className="py-2">
						<Input
							value={editTitle}
							onChange={(e) => setEditTitle(e.target.value)}
						/>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditingGroup(null)}>
							Abbrechen
						</Button>
						<Button
							onClick={handleSaveEdit}
							disabled={isUpdating || editTitle.trim().length === 0}
						>
							Speichern
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

export default GroupManagementTab;
